/**
 * SacredSites — /sacred-sites
 * Grid of Himachal Pradesh sacred sites with program callouts.
 */
import { useGlossary } from '../contexts/GlossaryContext';
import '../styles/SacredSites.css';

interface Props {
  onNavigate: (path: string) => void;
}

const SITES = [
  {
    id: 'masrur',
    name: { hi: 'मसरूर मंदिर', en: 'Masrur Rock-Cut Temple' },
    location: { hi: 'नगरोटा सूरियाँ, काँगड़ा', en: 'Nagrota Surian, Kangra' },
    era: { hi: '८वीं शताब्दी', en: '8th Century' },
    deity: { hi: 'शिव-पार्वती', en: 'Shiva–Parvati' },
    desc: {
      hi: 'पहाड़ी में तराशे गए ८वीं शती के शैव मंदिर — हिमाचल के एलोरा। संस्कृत अभिलेखों और वास्तु-शास्त्र का जीवंत उदाहरण।',
      en: 'Rock-cut 8th-century Shaiva temples — the Ellora of Himachal. A living example of Sanskrit inscriptions and Vastu Shastra.',
    },
    program: { hi: 'संस्कृत संवाद अभिलेख पाठ', en: 'Sanskrit inscription reading with Samvad' },
    image: 'https://res.cloudinary.com/df7tzagyl/image/upload/v1775693711/garuda_ajlfel.png',
  },
  {
    id: 'jwalamukhi',
    name: { hi: 'ज्वालामुखी देवी मंदिर', en: 'Jwalamukhi Devi Temple' },
    location: { hi: 'ज्वालामुखी, काँगड़ा', en: 'Jwalamukhi, Kangra' },
    era: { hi: 'प्राचीन — पुराण-वर्णित', en: 'Ancient — Puranic' },
    deity: { hi: 'ज्वालामुखी देवी (शक्तिपीठ)', en: 'Jwalamukhi Devi (Shakti Pitha)' },
    desc: {
      hi: 'भारत के ५१ शक्तिपीठों में से एक — देवी की जीभ यहाँ गिरी थी। प्राकृतिक अग्नि-ज्वालाओं का आध्यात्मिक महत्त्व।',
      en: 'One of 51 Shakti Pithas — where the goddess\'s tongue fell. Natural flame fissures of deep spiritual significance.',
    },
    program: { hi: 'देवी-स्तोत्र संस्कृत पाठ', en: 'Sanskrit Devi-stotra recitation' },
    image: 'https://res.cloudinary.com/df7tzagyl/image/upload/v1775693711/garuda_ajlfel.png',
  },
  {
    id: 'chamunda-devi',
    name: { hi: 'चामुण्डा देवी मंदिर', en: 'Chamunda Devi Temple' },
    location: { hi: 'पालमपुर, काँगड़ा', en: 'Palampur, Kangra' },
    era: { hi: 'मध्यकालीन', en: 'Medieval' },
    deity: { hi: 'चामुण्डा देवी', en: 'Chamunda Devi' },
    desc: {
      hi: 'बाणगंगा नदी के तट पर स्थित चामुण्डा देवी — दुर्गासप्तशती का प्रत्यक्ष दर्शन। धौलाधार के पृष्ठभूमि में पूजा।',
      en: 'Chamunda Devi on the Banganga river — Durga Saptashati in practice. Worship against the backdrop of Dhauladhar.',
    },
    program: { hi: 'धनुर्वेद — लक्ष्य साधना', en: 'Dhanurveda — focus practice nearby' },
    image: 'https://res.cloudinary.com/df7tzagyl/image/upload/v1775693711/garuda_ajlfel.png',
  },
  {
    id: 'triund',
    name: { hi: 'त्रियुण्ड — धौलाधार शिखर', en: 'Triund — Dhauladhar Peak' },
    location: { hi: 'मैक्लोडगंज, धर्मशाला', en: 'McLeodGanj, Dharamshala' },
    era: { hi: 'प्राकृतिक', en: 'Natural' },
    deity: { hi: 'शिव — प्रकृति-दर्शन', en: 'Shiva — Nature meditation' },
    desc: {
      hi: '२,८२७ मीटर पर बर्फाच्छादित धौलाधार का विहंगम दृश्य। ट्रेक में राजधर्म की एकाग्रता-साधना का अभ्यास।',
      en: '2,827m vantage point with panoramic snow-capped Dhauladhar. Trek as Rajadharma concentration practice.',
    },
    program: { hi: 'धनुर्वेद एकाग्रता ट्रेक', en: 'Dhanurveda concentration trek' },
    image: 'https://res.cloudinary.com/df7tzagyl/image/upload/q_auto/f_auto/v1775926720/Gemini_ijd695ijd695ijd6_upscayl_1x_upscayl-standard-4x_oxiptq.png',
  },
  {
    id: 'kangra-fort',
    name: { hi: 'काँगड़ा किला', en: 'Kangra Fort' },
    location: { hi: 'काँगड़ा', en: 'Kangra' },
    era: { hi: 'प्राचीन — कटोच राजवंश', en: 'Ancient — Katoch Dynasty' },
    deity: { hi: 'राजनीति — राजधर्म का क्षेत्र', en: 'Rajneeti — domain of Rajadharma' },
    desc: {
      hi: 'भारत के प्राचीनतम किलों में से एक — कटोच राजवंश का गढ़। राजधर्म के रक्षा-सिद्धांतों का ऐतिहासिक प्रमाण।',
      en: 'One of India\'s oldest forts — Katoch dynasty stronghold. Historical evidence of Rajadharma\'s defense principles.',
    },
    program: { hi: 'धनुर्वेद — क्षात्र-इतिहास', en: 'Dhanurveda — Kshatriya history' },
    image: 'https://res.cloudinary.com/df7tzagyl/image/upload/v1775693711/garuda_ajlfel.png',
  },
  {
    id: 'baijnath',
    name: { hi: 'बैजनाथ शिव मंदिर', en: 'Baijnath Shiva Temple' },
    location: { hi: 'बैजनाथ, काँगड़ा', en: 'Baijnath, Kangra' },
    era: { hi: '१३वीं शताब्दी', en: '13th Century' },
    deity: { hi: 'वैद्यनाथ (शिव)', en: 'Vaidyanatha (Shiva)' },
    desc: {
      hi: '१३वीं शती का नागर-शैली शिव मंदिर — नागर स्थापत्य और संस्कृत अभिलेखों से समृद्ध। ब्यास नदी तट पर।',
      en: '13th-century Nagara-style Shiva temple — rich in Nagara architecture and Sanskrit inscriptions. On the banks of Beas.',
    },
    program: { hi: 'संस्कृत संवाद — शिलालेख पाठ', en: 'Sanskrit Samvad — inscription reading' },
    image: 'https://res.cloudinary.com/df7tzagyl/image/upload/v1775693711/garuda_ajlfel.png',
  },
];

export default function SacredSites({ onNavigate }: Props) {
  const { t, locale } = useGlossary();
  const hi = locale === 'hi';

  const tl = (key: string, fallback: string) => {
    const v = t(key);
    return v === key ? fallback : v;
  };

  return (
    <div className="ss-page">
      {/* Hero */}
      <div className="ss-hero">
        <span className="brand-section-label">{tl('sites.label', 'Sacred Sites')}</span>
        <h1 className="ss-hero-title">
          {hi ? 'पवित्र स्थल — हिमाचल प्रदेश' : 'Sacred Sites — Himachal Pradesh'}
        </h1>
        <p className="ss-hero-sub">
          {hi
            ? 'धौलाधार की छाया में — मंदिर, किले और प्राकृतिक स्थल जो राजधर्म के कार्यक्रमों के साथ जुड़ते हैं।'
            : 'In the shadow of Dhauladhar — temples, forts, and natural sites that integrate with Rajadharma programs.'}
        </p>
        <button className="ss-btn-primary" onClick={() => onNavigate('/educational-trips')}>
          {hi ? '🏕️ शैक्षिक यात्रा देखें' : '🏕️ View Educational Trips'}
        </button>
      </div>

      {/* Sites grid */}
      <section className="ss-section">
        <div className="ss-grid">
          {SITES.map(site => (
            <div key={site.id} className="ss-card">
              <div className="ss-card-img" style={{ backgroundImage: `url(${site.image})` }} />
              <div className="ss-card-body">
                <div className="ss-card-era">{hi ? site.era.hi : site.era.en}</div>
                <h3 className="ss-card-name">{hi ? site.name.hi : site.name.en}</h3>
                <p className="ss-card-location">📍 {hi ? site.location.hi : site.location.en}</p>
                <p className="ss-card-deity">🙏 {hi ? site.deity.hi : site.deity.en}</p>
                <p className="ss-card-desc">{hi ? site.desc.hi : site.desc.en}</p>
                <div className="ss-program-callout">
                  <span className="trust-badge">🏕️ {hi ? site.program.hi : site.program.en}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="ss-cta-section">
        <p>
          {hi
            ? 'इन सभी स्थलों को शैक्षिक यात्राओं में शामिल किया जा सकता है। अपनी यात्रा अभी बुक करें।'
            : 'All these sites can be included in educational trips. Book your journey now.'}
        </p>
        <div className="ss-cta-actions">
          <button className="ss-btn-primary" onClick={() => onNavigate('/booking')}>
            {hi ? '📋 यात्रा बुक करें' : '📋 Book a Trip'}
          </button>
          <button className="ss-btn-ghost" onClick={() => onNavigate('/impact')}>
            {hi ? '📊 प्रभाव देखें' : '📊 View Impact'}
          </button>
        </div>
      </section>
    </div>
  );
}
